import {
  useMutation,
  useQuery,
  useSuspenseQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  addDays,
  eatDayOf,
  formatEatDateTime,
  formatEatTime,
  formatIsoDay,
  formatPeriod,
  parseIsoDay,
  type CalendarDay,
} from '@wodi/domain';
import { REPORT_ACCESS, type ReportKey, type ReportPeriod } from '@wodi/shared';
import {
  Button,
  DateField,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  Notice,
  PrintFooter,
  PrintHeader,
  PrintSheet,
  printSheet,
  Select,
} from '@wodi/ui';
import { useRef, useState, type ReactNode, type RefObject } from 'react';

import { downloadReport, reportFacilitiesQuery, reportPrinted } from '../api/reports.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './reports.module.css';

// The shared frame of every report page (docs/plans/phase1-reports.md §9.2): filters in
// component state (never the address), "Show report", "As at", Print and Download CSV,
// and the loading, empty, error and offline states.

export const ALL_FACILITIES = 'all';

export function todayIso(now: Date = new Date()): string {
  return formatIsoDay(eatDayOf(now));
}

export function yesterdayIso(now: Date = new Date()): string {
  return formatIsoDay(addDays(eatDayOf(now), -1));
}

export function dayOf(iso: string): CalendarDay | null {
  const parsed = parseIsoDay(iso);
  return parsed.ok ? parsed.value : null;
}

/** `22 Sep 2026` or `01 Sep 2026 to 30 Sep 2026`. */
export function periodWords(period: ReportPeriod): string {
  const from = dayOf(period.from);
  const to = dayOf(period.to);
  return from === null || to === null ? '' : formatPeriod({ from, to });
}

/** The facilities this report may cover, and the choice "All my facilities" when several. */
export function useFacilityChoice(key: ReportKey) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const facilities = useQuery(reportFacilitiesQuery);
  const permitted = (facilities.data?.facilities ?? []).filter((f) => f.reports.includes(key));
  const current = session?.facility.id ?? '';
  const options = [
    ...(permitted.length > 1 ? [{ id: ALL_FACILITIES, label: 'All my facilities' }] : []),
    ...permitted.map((f) => ({ id: f.facilityId, label: f.name })),
  ];
  return {
    options,
    current,
    /** The body field for a choice: omitted for the current facility. */
    idsFor: (choice: string): string[] | undefined =>
      choice === ALL_FACILITIES
        ? permitted.map((f) => f.facilityId)
        : choice === current
          ? undefined
          : [choice],
  };
}

export function FacilityFilter({
  reportKey,
  value,
  onChange,
}: {
  reportKey: ReportKey;
  value: string;
  onChange: (id: string) => void;
}) {
  const choice = useFacilityChoice(reportKey);
  if (choice.options.length < 2) return null;
  return (
    <Select
      label="Facility"
      options={choice.options}
      value={value}
      onChange={(v) => {
        onChange(String(v));
      }}
    />
  );
}

export function PeriodFields({
  period,
  onChange,
  maxDays,
}: {
  period: ReportPeriod;
  onChange: (period: ReportPeriod) => void;
  maxDays: number;
}) {
  const from = dayOf(period.from);
  const to = dayOf(period.to);
  const backwards = from !== null && to !== null && period.from > period.to;
  const days =
    from !== null && to !== null && !backwards
      ? Math.round((Date.parse(period.to) - Date.parse(period.from)) / 86_400_000) + 1
      : 0;
  return (
    <>
      <DateField
        label="From"
        value={period.from}
        onChange={(v) => {
          if (v !== null) onChange({ ...period, from: v });
        }}
      />
      <DateField
        label="To"
        value={period.to}
        onChange={(v) => {
          if (v !== null) onChange({ ...period, to: v });
        }}
        {...(backwards
          ? { errorMessage: 'Choose a day on or after the first day.' }
          : days > maxDays
            ? { errorMessage: `Choose ${String(maxDays)} days or fewer.` }
            : {})}
      />
    </>
  );
}

export function periodIsValid(period: ReportPeriod, maxDays: number): boolean {
  if (dayOf(period.from) === null || dayOf(period.to) === null || period.from > period.to)
    return false;
  return Math.round((Date.parse(period.to) - Date.parse(period.from)) / 86_400_000) + 1 <= maxDays;
}

/** "As at 08:14" for today's snapshot, with the date when it is another day. */
export function AsAt({ dataAsAt }: { dataAsAt: string }) {
  const instant = new Date(dataAsAt);
  const sameDay = formatIsoDay(eatDayOf(instant)) === todayIso();
  return (
    <p className={styles.muted}>
      {`As at ${sameDay ? formatEatTime(instant) : formatEatDateTime(instant)}`}
    </p>
  );
}

/**
 * Loading, error, offline and empty states around a report's result. When the connection
 * drops, the last result stays on screen marked "May be out of date".
 */
export function ReportStates<T>({
  query,
  what,
  isEmpty,
  emptyText,
  children,
}: {
  query: UseQueryResult<T>;
  what: string;
  isEmpty: (data: T) => boolean;
  emptyText: string;
  children: (data: T) => ReactNode;
}) {
  const online = useOnline();
  return (
    <>
      {!online ? (
        <Notice tone="info">
          {query.data === undefined
            ? "You're offline. Reports need a connection."
            : "You're offline. Reports need a connection. What's shown may be out of date."}
        </Notice>
      ) : null}
      {query.isPending && query.fetchStatus !== 'idle' ? (
        <LoadingBlock label={`Loading ${what}`} rows={4} />
      ) : null}
      {query.isError && online ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void query.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(query.error, `The ${what} couldn't be loaded. Try again.`)}
        </Notice>
      ) : null}
      {query.data === undefined ? null : isEmpty(query.data) ? (
        <EmptyState>{emptyText}</EmptyState>
      ) : (
        children(query.data)
      )}
    </>
  );
}

/** Print (audited first, then the browser's dialog) and Download CSV (audited on the server). */
export function ReportActions({
  reportKey,
  sheet,
  printBody,
  exportBody,
  disabled,
}: {
  reportKey: ReportKey;
  sheet: RefObject<HTMLElement | null>;
  printBody: { period: ReportPeriod; facilityIds: string[]; rowCount: number } | null;
  exportBody: object | null;
  disabled: boolean;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const [confirming, setConfirming] = useState(false);
  const listsPatients = REPORT_ACCESS[reportKey].listsPatients;
  const mayExport = session !== null && can(session, 'reports.export');

  const print = useMutation({
    mutationFn: async () => {
      if (printBody === null) return;
      await reportPrinted(reportKey, printBody);
    },
    onSuccess: () => {
      if (sheet.current !== null) printSheet(sheet.current);
    },
    onError: (error) => {
      toasts.show({ title: 'Not printed', description: errorText(error, 'Try again.') });
    },
  });
  const download = useMutation({
    mutationFn: () => downloadReport(reportKey, exportBody ?? {}),
    onSuccess: () => {
      setConfirming(false);
      toasts.show({ title: 'Download started' });
    },
  });
  const unavailable = disabled || !online || printBody === null;
  return (
    <div className={styles.actions}>
      <Button
        onPress={() => {
          print.mutate();
        }}
        isDisabled={unavailable}
        isPending={print.isPending}
        pendingLabel="Printing"
      >
        Print
      </Button>
      {mayExport ? (
        <Button
          onPress={() => {
            if (listsPatients) setConfirming(true);
            else download.mutate();
          }}
          isDisabled={unavailable || exportBody === null}
          isPending={download.isPending && !confirming}
          pendingLabel="Downloading"
        >
          Download CSV
        </Button>
      ) : null}
      {download.isError && !confirming ? (
        <Notice tone="error">
          {errorText(download.error, "The file wasn't downloaded. Try again.")}
        </Notice>
      ) : null}
      <Dialog
        title="Download a file that lists patients"
        isOpen={confirming}
        onOpenChange={setConfirming}
      >
        {({ close }) => (
          <>
            <p>
              This file lists patients. Keep it on facility devices and delete it when you're done.
            </p>
            {download.isError ? (
              <Notice tone="error">
                {errorText(download.error, "The file wasn't downloaded. Try again.")}
              </Notice>
            ) : null}
            <DialogActions>
              <Button onPress={close}>Cancel</Button>
              <Button
                variant="primary"
                onPress={() => {
                  download.mutate();
                }}
                isPending={download.isPending}
                pendingLabel="Downloading"
              >
                Download
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </div>
  );
}

/** The A4 sheet a report prints on. Kept off screen; `printSheet` shows only it on paper. */
export function PrintableReport({
  sheet,
  title,
  facility,
  period,
  dataAsAt,
  children,
}: {
  sheet: RefObject<HTMLElement | null>;
  title: string;
  facility: string;
  period: string;
  dataAsAt: string;
  children: ReactNode;
}) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  return (
    <div className={styles.printOnly} aria-hidden="true">
      <PrintSheet paper="a4" label={title} ref={sheet}>
        <PrintHeader facility={facility} facilityLines={[period]} title={title} />
        {children}
        <PrintFooter>
          {`As at ${formatEatDateTime(new Date(dataAsAt))}. Printed by ${session?.user.displayName ?? ''} at ${formatEatDateTime(new Date())}.`}
        </PrintFooter>
      </PrintSheet>
    </div>
  );
}

export function useSheet() {
  return useRef<HTMLElement>(null);
}

export function BackToReports() {
  return <Link to="/reports">All reports</Link>;
}

/** Facility names for a print header: one name, or "3 facilities". */
export function facilityWords(names: readonly string[]): string {
  if (names.length === 1) return names[0] ?? '';
  return `${String(names.length)} facilities`;
}
